// @flow strict
import type { State } from '../state-list.js';

import * as React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import AchievementLevelPanel from '../achievement-level-panel.jsx';
import styles from './skills-page.css';
import buttonStyles from '../button.css';
import iconStyles from '../icons.css';
import arrowBackSvg from '../images/ic_arrow_back_24px.svg';
import arrowForwardSvg from '../images/ic_arrow_forward_24px.svg';
import svgImageFn from '../svg-image.jsx';
import ParamSelector from '../ald-selection.jsx';
import refreshSvg from '../images/ic_refresh_24px.svg';
import { loadThemeForPartner } from '../theme-loader.js';
import visibilityStyles from '../components/visibility.css';
import visibilityFn from '../components/visibility.jsx';
import {
  buildGridRowTemplate,
  calculateLeftValue,
} from './skills-page.helpers.js';
import { useEffect, useState } from './skills-page.hooks.js';
import { Partner } from '../partner.js';
import type { Grade } from '../ald-types'

export type SkillsPageProps = {|
  grades: Grade[],
  onDataRefresh: (string, string, string) => void,
  selectedAchievementLevel: ?string,
  selectedGradeId: ?string,
  selectedPartner: ?Partner,
  selectedSubjectId: ?string,
  stateList: State[],
|};

const ArrowBack = svgImageFn(iconStyles.mediumSquare, '', arrowBackSvg);
const ArrowForward = svgImageFn(iconStyles.mediumSquare, '', arrowForwardSvg);
const Refresh = svgImageFn(iconStyles.mediumSquare, '', refreshSvg);
const Visibility = visibilityFn(
  visibilityStyles.visible,
  visibilityStyles.removed
);

const SkillsPage = ({
  onDataRefresh,
  selectedAchievementLevel,
  selectedGradeId,
  selectedPartner,
  selectedSubjectId,
  stateList,
}: SkillsPageProps): React.Node => {
  const partner = selectedPartner??Partner.Default;
  if (!selectedPartner || !selectedGradeId || !selectedSubjectId) {
    return <Navigate to="/" />;
  }

  const state = useState(selectedPartner, selectedGradeId, selectedSubjectId);
  const navigate = useNavigate();
  useEffect(
    selectedPartner,
    selectedGradeId,
    selectedSubjectId,
    selectedAchievementLevel,
    state
  );

  if (state.aldData.length === 0) {
    return <div>Loading...</div>;
  }

  const changeStrandDisplayStatus = (strand: string) => {
    const newStrandDisplayStatus = {
      ...state.strandDisplayStatus,
    };
    newStrandDisplayStatus[strand] =
      state.strandDisplayStatus[strand] === 'expanded'
        ? 'collapsed'
        : 'expanded';
    state.setStrandDisplayStatus(newStrandDisplayStatus);
  };

  const onAllDataSelected = (
    partnerId: string,
    gradeId: string,
    subjectId: string
  ) => {
    state.setNextDataSelection({
      gradeId,
      partnerId,
      subjectId,
    });
    state.setRefreshEnabled(true);
  };
  const onRefresh = (event:SyntheticEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const partnerId = state.nextDataSelection.partnerId;
    const gradeId = state.nextDataSelection.gradeId;
    const subjectId = state.nextDataSelection.subjectId;
    if (partnerId && gradeId && subjectId) {
      onDataRefresh(partnerId, gradeId, subjectId);
      navigate('/display-skills', {
        state: {
          selectedGradeId: gradeId,
          selectedPartnerId: partnerId,
          selectedSubjectId: subjectId,
        }
      });
    }
  };
  const theme = loadThemeForPartner(partner);
  const logo = theme.getLogo();

  return (
    <main>
      {/* main page */}
      <section aria-label="Overview">
        {/* Welcome and filter */}
        <div
          className={`${
            styles.welcomePanel
          } ${theme.getWelcomePanelOverrideStyle()}`}
          data-cy="welcomePanel"
        >
          <Visibility visible={logo !== null}>{logo}</Visibility>
          <Visibility visible={logo === null}>
            <h2 className="nwea-ds-v1-typography-heading-5">
              <span className={styles.lightGreyHeader}>Welcome to the</span>
              <br />
              <span className={styles.purpleHeader}>
                Achievement Level Explorer
              </span>
            </h2>
          </Visibility>
          <p className="nwea-ds-v1-typography-body-copy-medium">
            This resource shows your state&apos;s standards organized by
            Achievement Levels, which are a summary of a student&apos;s
            grade-level performance based on your state&apos;s content area
            standards.
          </p>
        </div>
        <div className={styles.guidencePanel} data-cy="achieveLevels">
          <h1 className="nwea-ds-v1-typography-heading-5" data-cy="heading">
            Student Achievement Levels
          </h1>
          <p
            className="nwea-ds-v1-typography-body-copy-medium"
            data-cy="bodyParaText"
          >
            Review the Achievement Levels ({state.skills.map((skill)=>skill.achievementLevelConfig.name).join(', ')})
            and corresponding Achievement Level Descriptors (ALDs) below.
          </p>
        </div>
      </section>

      <div
        className={styles.selectionPanel}
        data-cy="selectionControls"
        aria-labelledby="currentlyViewingLabel"
      >
        <p
          className={`nwea-ds-v1-typography-caption ${styles.currentlyViewing}`}
          data-cy="currently-viewing"
        >
          Currently Viewing:
        </p>
        <ParamSelector
          data={stateList}
          onAllDataSelected={onAllDataSelected}
          orientation="horizontal"
          selectedPartnerId={(selectedPartner:string)}
          selectedGradeId={selectedGradeId}
          selectedSubjectId={selectedSubjectId}
        />
        <nwea-ds-v1-button
          color="purple"
          size="large"
          focus-override={true}
          disabled={state.refreshEnabled ? undefined : true}
          onClick={onRefresh}
          ds-aria-label="load selected parameters"
          data-cy="refresh-button"
        >
          <Refresh />
        </nwea-ds-v1-button>
      </div>
      <div className={styles.carouselContainer}>
        <nwea-ds-v1-button
          color="purple"
          size="large"
          focus-override={true}
          class={buttonStyles.moveLeft}
          disabled={state.firstPanel <= 0 ? true : undefined}
          ds-aria-label={
            state.firstPanel <= 0
              ? 'no panels to the left'
              : 'move left one panel'
          }
          data-cy="left-arrow"
          onClick={() => {
            state.setFirstPanel(state.firstPanel - 1);
          }}
        >
          <ArrowBack />
        </nwea-ds-v1-button>
        <section
          className={styles.achievementLevelCarousel}
          aria-label="Achievement Levels"
          style={{
            left: calculateLeftValue(state.firstPanel),
            gridTemplateRows: buildGridRowTemplate(state.strandIndicatorGroups),
          }}
        >
          {state.skills.map((skill, i) => {
            return (
              <AchievementLevelPanel
                key={skill.achievementLevelConfig.name}
                achievementLevel={skill}
                nextAchievementLevelName={
                  i < state.skills.length - 1
                    ? state.skills[i + 1].achievementLevelConfig.name
                    : 'next'
                }
                isFirstPanel={i === state.firstPanel}
                panelNum={i}
                numPanels={state.skills.length}
                dimmed={i < state.firstPanel || i > state.firstPanel + 1}
                strandDisplayStatus={state.strandDisplayStatus}
                changeStrandDisplayStatus={changeStrandDisplayStatus}
                strandIndicatorGroups={state.strandIndicatorGroups}
              />
            );
          })}
        </section>
        <nwea-ds-v1-button
          color="purple"
          size="large"
          focus-override={true}
          class={buttonStyles.moveRight}
          disabled={
            state.firstPanel >= state.skills.length - 2 ? true : undefined
          }
          ds-aria-label={
            state.firstPanel >= state.skills.length - 2
              ? 'no panels to the right'
              : 'move right one panel'
          }
          data-cy="right-arrow"
          onClick={() => {
            state.setFirstPanel(state.firstPanel + 1);
          }}
        >
          <ArrowForward />
        </nwea-ds-v1-button>
      </div>
    </main>
  );
};

export default SkillsPage;
